/**
 * PD-14845 — "primary" license resolution.
 *
 * A client can hold several licenses of the same type at one address
 * (PD-14844). Exactly one of them is the "primary" — the license the ERP
 * integration and order validation act on. This module resolves which one,
 * from a group already scoped to a single (address, type).
 *
 * Precedence, highest first:
 *   0. in force beats not in force (PD-17159): when the group holds at least
 *      one in-force license AND at least one that is not, steps 1-4 run over
 *      the in-force rows only. An in-force license therefore wins its type's
 *      slot even over an expired row a user flagged primary (Chris,
 *      2026-10-26, PD-17154 decision 3: "the ACTIVE one is the one that
 *      integrates back to the ERP"). A group that is all in force, all not in
 *      force, or whose caller supplies no `inForce`, resolves as before.
 *   1. an explicitly-flagged primary (`isPrimary`)      — the user's choice
 *   2. the only license in the group                    — implicitly primary
 *   3. the sole ERP-sourced row (non-null `externalId`) — the ERP license
 *   4. otherwise `null`                                 — ambiguous; a user must pick
 *
 * Twins that must change with this rule: the client-list license columns
 * (apps/crm-api/src/modules/clients/client-license-field-sql.ts) and the ERP
 * export's hand port in mover-integration
 * (ClientLicensePrimaryService.selectLicenseToExport).
 */

import { statusCanOrder } from '../constants/license-status-canonical';

export interface PrimaryLicenseCandidate {
	externalId: string | null;
	isPrimary?: boolean | null;
	/**
	 * PD-17159 — whether the license is in force, from
	 * `isClientLicenseInForce`. Only `true` counts as in force: `false`,
	 * `null` (status unknown) and an omitted value all count as not in force,
	 * the same way order validation treats an unknown status as unable to order
	 * (PD-14745). A caller that never sets it gets the pre-PD-17159 behavior,
	 * because a group with no in-force row is never narrowed.
	 */
	inForce?: boolean | null;
}

/** The license fields the effective status is read from. */
export interface ClientLicenseStatusInputModel {
	/** The license's own status (`client_license.status`). */
	status?: string | null;
	/**
	 * The linked registry row's canonical status: `lm_registry.
	 * license_status_normalized`, or its tenant cache `client_license.
	 * registry_status`. Null or blank when the license is not linked.
	 */
	registryStatus?: string | null;
}

/**
 * PD-17159 — is this license in force for primary resolution? Effective status
 * is the linked registry row's status, else the license's own, judged by the
 * canonical can-order table (Active, Probation, Limited), the same rule
 * tools/probes/PD-17154/erp-precedence.sql reads from lm_status_canonical.
 * Unknown or blank statuses are not in force.
 */
export function isClientLicenseInForce(
	license: ClientLicenseStatusInputModel,
): boolean {
	const registryStatus = license.registryStatus?.trim() ?? '';
	return statusCanOrder(
		registryStatus !== '' ? registryStatus : (license.status ?? null),
	);
}

/**
 * The in-force rows when the group mixes in-force and not-in-force licenses,
 * else the whole group unchanged (PD-17159 step 0).
 */
function narrowToInForce<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): ReadonlyArray<T> {
	const inForce = group.filter((license) => license.inForce === true);
	return inForce.length > 0 && inForce.length < group.length ? inForce : group;
}

/**
 * Resolve the primary license within a group scoped to ONE address and ONE
 * type. Returns the winning row, or `null` when the choice is ambiguous (2+
 * licenses with no flag and no single ERP row). Callers MUST pre-scope the
 * input; this function does not group by address or type.
 */
export function resolvePrimaryLicense<T extends PrimaryLicenseCandidate>(
	fullGroup: ReadonlyArray<T>,
): T | null {
	// 0. In force beats not in force (PD-17159): a mixed group chooses among
	//    its in-force rows only, so an expired row can never take the slot
	//    from an in-force one, whatever flag or ERP id it carries.
	const group = narrowToInForce(fullGroup);
	// 1. An explicit user flag always wins. At most one primary per group is
	//    DB-enforced; if two are somehow flagged, treat as ambiguous rather
	//    than picking arbitrarily.
	const flagged = group.filter((license) => license.isPrimary === true);
	if (flagged.length === 1) {
		return flagged[0] ?? null;
	}
	if (flagged.length > 1) {
		return null;
	}
	// 2. A lone license of the type is primary implicitly (no flag needed).
	if (group.length === 1) {
		return group[0] ?? null;
	}
	// 3. The sole ERP-sourced row (the upstream integration writes exactly one
	//    externalId-bearing row per type — verified in the data).
	const erpRows = group.filter(
		(license) => license.externalId != null && license.externalId !== '',
	);
	if (erpRows.length === 1) {
		return erpRows[0] ?? null;
	}
	// 4. Ambiguous — a primary must be designated explicitly.
	return null;
}

/**
 * PD-17159 — the row a user flagged primary when the resolver chose another
 * row instead (an in-force license displaced a not-in-force flagged one), so
 * the UI can show that flag as overridden. Null when the flag is honored, when
 * nothing is flagged, or when two rows are flagged (ambiguous, not overridden).
 */
export function overriddenPrimaryLicense<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): T | null {
	const flagged = group.filter((license) => license.isPrimary === true);
	if (flagged.length !== 1) {
		return null;
	}
	const flaggedRow = flagged[0] ?? null;
	return flaggedRow != null && resolvePrimaryLicense(group) !== flaggedRow
		? flaggedRow
		: null;
}

/**
 * Whether the "primary" concept is meaningful for this (address, type) group:
 * true once there are two or more licenses, so the UI can offer a "Make
 * primary" control. A lone license is always implicitly primary and needs no
 * affordance.
 */
export function isPrimaryEligible<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): boolean {
	return group.length >= 2;
}

/**
 * Whether a primary MUST be chosen explicitly for this group — 2+ licenses and
 * no primary can be derived automatically (no flag, and not a lone ERP row).
 * This is the "needs attention" case the ERP integration declines to act on.
 */
export function requiresPrimarySelection<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): boolean {
	return group.length >= 2 && resolvePrimaryLicense(group) == null;
}

/** The license fields `overriddenPrimaryLicenseIds` groups and resolves on. */
export interface PrimaryOverrideInputModel extends ClientLicenseStatusInputModel {
	id: number;
	type: string;
	fkClientAddressId?: number | null;
	externalId?: string | null;
	isPrimary?: boolean | null;
}

/**
 * PD-17159 — the ids of licenses whose primary flag an in-force license
 * overrode, over a client's license rows of any address and type. Groups by
 * (address, type), the scope the flag is held in, so a list screen can mark
 * those rows without re-implementing the grouping.
 */
export function overriddenPrimaryLicenseIds(
	licenses: ReadonlyArray<PrimaryOverrideInputModel>,
): Set<number> {
	const groups = new Map<
		string,
		Array<PrimaryLicenseCandidate & { id: number }>
	>();
	for (const license of licenses) {
		const key = `${license.fkClientAddressId ?? 'none'}::${license.type}`;
		const group = groups.get(key) ?? [];
		group.push({
			id: license.id,
			externalId: license.externalId ?? null,
			isPrimary: license.isPrimary ?? null,
			inForce: isClientLicenseInForce(license),
		});
		groups.set(key, group);
	}
	const ids = new Set<number>();
	for (const group of groups.values()) {
		const overridden = overriddenPrimaryLicense(group);
		if (overridden != null) {
			ids.add(overridden.id);
		}
	}
	return ids;
}
